import {
  alpha,
  createStyles,
  makeStyles,
  type Theme,
} from '@material-ui/core/styles';
import { artifactJourneyStyles } from './artifactJourneyStyles';
import { cockpitType } from './cockpitTokens';
import { readerStyles } from './readerStyles';
import { evidenceStyles } from './evidenceStyles';
import { timelineStyles } from './timelineStyles';

const shellStyles = (theme: Theme) =>
  createStyles({
    // grid items default to min-width:auto, so one wide child (the stepper's
    // max-content track) would widen every section; zero lets them shrink
    shell: {
      display: 'grid',
      gap: theme.spacing(2.5),
      '& > *': { minWidth: 0 },
    },
    panel: { padding: theme.spacing(3) },
    // the coverage panel carries the page's claim, so it is the only one that
    // states its allegiance in the frame itself
    panelLead: {
      padding: theme.spacing(3),
      borderTop: `3px solid ${theme.palette.secondary.main}`,
    },
    sectionTitle: { ...cockpitType.sectionTitle },
    // one rule under every section title, so the sections read as siblings
    sectionHead: {
      display: 'grid',
      gap: theme.spacing(0.5),
      paddingBottom: theme.spacing(1.5),
      marginBottom: theme.spacing(2.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    sectionLede: { color: theme.palette.text.secondary },
    summary: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    // Backstage's header does not constrain this row, so without an explicit
    // wrap the last action leaves the viewport instead of moving to a new line
    headerActions: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      alignItems: 'center',
      justifyContent: 'flex-end',
      maxWidth: '100%',
      minWidth: 0,
      // narrow enough for the subtitle to wrap, the actions land on the header's
      // second flex line, where its negative grid margin pulls them over the
      // subtitle; the offset clears it and left-aligns them under the title
      [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing(2),
        justifyContent: 'flex-start',
      },
    },
    headerChip: {
      color: 'inherit',
      borderColor: 'currentColor',
    },
    sectionIntro: { marginBottom: theme.spacing(2) },
    // a heading assistive technology needs and sighted readers do not: the
    // stepper itself is the title of its section
    visuallyHidden: {
      position: 'absolute',
      width: 1,
      height: 1,
      padding: 0,
      margin: -1,
      overflow: 'hidden',
      clip: 'rect(0 0 0 0)',
      whiteSpace: 'nowrap',
      border: 0,
    },
  });

// Seven phases on one line, connected; on a narrow viewport the line scrolls
// rather than wraps, so the sequence keeps reading left to right.
const phaseStepperStyles = (theme: Theme) =>
  createStyles({
    phaseStepperScroll: {
      overflowX: 'auto',
      overflowY: 'hidden',
      maxWidth: '100%',
      minWidth: 0,
    },
    phaseStepper: {
      display: 'flex',
      alignItems: 'flex-start',
      listStyle: 'none',
      margin: 0,
      padding: 0,
      minWidth: 'max-content',
      width: '100%',
    },
    phaseStep: {
      position: 'relative',
      flex: '1 1 0',
      minWidth: 96,
      display: 'grid',
      justifyItems: 'center',
      gap: theme.spacing(0.75),
      padding: theme.spacing(0, 1),
      color: theme.palette.text.secondary,
      // the connector runs behind the icons, from one step's center to the next
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 11,
        left: '-50%',
        width: '100%',
        height: 1,
        background: theme.palette.divider,
      },
      '&:first-child::before': { display: 'none' },
    },
    phaseStepActive: {
      color: theme.palette.text.primary,
      fontWeight: 600,
    },
    // the whole step is the target; it inherits the step's grid so the icon
    // and label sit exactly where a static step puts them
    phaseStepButton: {
      display: 'grid',
      justifyItems: 'center',
      gap: theme.spacing(0.75),
      width: '100%',
      padding: theme.spacing(0.5, 0),
      border: 0,
      background: 'transparent',
      color: 'inherit',
      font: 'inherit',
      cursor: 'pointer',
      borderRadius: theme.shape.borderRadius,
      '&:hover': { background: theme.palette.action.hover },
      '&[aria-pressed="true"]': {
        boxShadow: `inset 0 -3px 0 ${theme.palette.secondary.main}`,
      },
      '&:focus-visible': {
        outline: `3px solid ${theme.palette.secondary.main}`,
        outlineOffset: 2,
      },
    },
    phaseStepLabel: {
      fontSize: '0.8125rem',
      lineHeight: 1.3,
      textAlign: 'center',
    },
    phaseIcon: {
      position: 'relative',
      zIndex: 1,
      fontSize: 22,
      background: theme.palette.background.paper,
      borderRadius: '50%',
    },
    phaseIconCompleted: { color: theme.palette.primary.main },
    phaseIconActive: { color: theme.palette.secondary.main },
    phaseIconPending: { color: theme.palette.action.disabled },
  });

// A row of measurements is one object. Boxing each cell tripled the border
// count without adding a single fact, so the cells are divided, not framed.
const metricStyles = (theme: Theme) =>
  createStyles({
    metricGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      columnGap: theme.spacing(3),
      rowGap: theme.spacing(3),
    },
    // whitespace separates the columns; the numerals are heavy enough to hold
    // them without rules, and the first value stays flush with the section title
    metric: {
      display: 'grid',
      gap: theme.spacing(0.75),
      alignContent: 'start',
      minWidth: 0,
    },
    metricLabel: {
      ...cockpitType.eyebrow,
      color: theme.palette.text.secondary,
      // two lines are reserved so a label that wraps does not drop its own
      // numeral below the rest of the row
      minHeight: `${cockpitType.eyebrow.lineHeight * 2}em`,
    },
    metricValueRow: {
      display: 'flex',
      gap: theme.spacing(0.75),
      alignItems: 'baseline',
      flexWrap: 'wrap',
      minWidth: 0,
    },
    metricValue: {
      ...cockpitType.numeral,
      overflowWrap: 'anywhere',
    },
    metricUnit: {
      color: theme.palette.text.secondary,
      overflowWrap: 'anywhere',
    },
  });

// The expandable decomposition inside a metric card: a plain button plus the
// region it discloses. No transition is defined, so reduced-motion preference
// is respected for free — there is nothing to suppress.
const metricDetailsStyles = (theme: Theme) =>
  createStyles({
    metricDetails: { marginTop: theme.spacing(0.5) },
    metricDetailsToggle: {
      font: 'inherit',
      color: theme.palette.secondary.main,
      background: 'none',
      border: 'none',
      padding: 0,
      cursor: 'pointer',
      textDecoration: 'underline',
      '&:focus-visible': {
        outline: `3px solid ${theme.palette.secondary.main}`,
        outlineOffset: 2,
      },
    },
    metricDetailsRegion: {
      marginTop: theme.spacing(1),
      display: 'grid',
      gap: theme.spacing(1.5),
      minWidth: 0,
    },
    metricParcelList: {
      display: 'grid',
      gap: theme.spacing(0.5),
      margin: 0,
    },
    metricParcelRow: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      '& dt': { color: theme.palette.text.secondary },
      '& dd': { margin: 0, fontWeight: 600 },
    },
    metricBreakdownTable: {
      // the table scrolls on its own axis; the page never gains a horizontal
      // scrollbar because of a wide breakdown on a narrow viewport
      overflowX: 'auto',
      maxWidth: '100%',
    },
    metricLegacyNotice: {
      borderLeft: `3px solid ${theme.palette.text.secondary}`,
      paddingLeft: theme.spacing(1),
    },
  });

const metricNoticeStyles = (theme: Theme) =>
  createStyles({
    contextualNotice: {
      borderLeft: `3px solid ${theme.palette.warning.main}`,
      background: alpha(theme.palette.warning.main, 0.07),
      borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
      overflowWrap: 'anywhere',
      padding: theme.spacing(1, 1.5),
    },
    contextualNoticeError: {
      borderLeftColor: theme.palette.error.main,
      background: alpha(theme.palette.error.main, 0.07),
    },
    metricStatus: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(0.75),
      margin: theme.spacing(1, 0),
    },
    progressiveDetails: {
      marginTop: theme.spacing(1),
      '& summary': { cursor: 'pointer', fontWeight: 600 },
      '& summary:focus-visible': {
        outline: `3px solid ${theme.palette.secondary.main}`,
        outlineOffset: 2,
      },
    },
  });

// The bar is one object: a title row with its disclosure, then the five
// facts divided by whitespace, then the single notice. Nothing is boxed.
const metricsBarStyles = (theme: Theme) =>
  createStyles({
    metricsBarHead: {
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      paddingBottom: theme.spacing(1.5),
      marginBottom: theme.spacing(2.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    metricBar: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
      columnGap: theme.spacing(3),
      rowGap: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    metricCoverage: {
      display: 'grid',
      gap: theme.spacing(1),
      paddingTop: theme.spacing(2),
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    metricCoverageTitle: { margin: 0 },
  });

const guaranteeStyles = (theme: Theme) =>
  createStyles({
    guaranteeImpact: { marginTop: theme.spacing(1) },
    guaranteeGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
      gap: theme.spacing(1),
    },
    controlCard: {
      padding: theme.spacing(1.5),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      display: 'grid',
      gap: theme.spacing(0.75),
    },
  });

const sessionObservabilityStyles = (theme: Theme) =>
  createStyles({
    sessionPanel: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      display: 'grid',
      gap: theme.spacing(1.5),
    },
    sessionCapabilityGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: theme.spacing(1),
      listStyle: 'none',
      margin: 0,
      padding: 0,
    },
    sessionCapability: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      display: 'grid',
      gap: theme.spacing(0.5),
      padding: theme.spacing(1),
    },
    sessionJointDiagnostic: {
      borderLeft: `${theme.spacing(0.5)}px solid ${theme.palette.warning.main}`,
      display: 'grid',
      gap: theme.spacing(0.5),
      padding: theme.spacing(1),
    },
  });

export const useCockpitStyles = makeStyles(theme =>
  createStyles({
    ...shellStyles(theme),
    ...phaseStepperStyles(theme),
    ...evidenceStyles(theme),
    ...artifactJourneyStyles(theme),
    ...metricStyles(theme),
    ...metricDetailsStyles(theme),
    ...metricNoticeStyles(theme),
    ...metricsBarStyles(theme),
    ...guaranteeStyles(theme),
    ...sessionObservabilityStyles(theme),
    ...timelineStyles(theme),
    ...readerStyles(theme),
  }),
);
